"use client"

import { useEffect } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useTypedFeatureFlag } from "@/hooks/use-feature-flag"
import { CheckCircle, Mail, Sparkles } from "lucide-react"

import { useSubscription } from "../hooks/use-subscription"
import { useBillingStore } from "../store/billing-store"
import type { Plan } from "../types"

type Feature = {
  name: string
  icon: React.ReactNode
  disabled?: boolean
  bold?: boolean
}

const FEATURES_BY_PLAN_TYPE: Record<string, Feature[]> = {
  basic: [
    {
      name: "Agendamentos automáticos 24h por dia",
      icon: <Mail />,
    },
    {
      name: "Leads qualificados direto no WhatsApp",
      icon: <Mail />,
    },
    {
      name: "Aumento de até 50% nos agendamentos",
      icon: <Mail />,
    },
    {
      name: "Lembretes que reduzem faltas",
      icon: <Mail />,
    },
    {
      name: "Follow-ups automatizados que reativam conversas",
      icon: <Mail />,
    },
    {
      name: "IA integrada com sua equipe",
      icon: <Mail />,
    },
    {
      name: "Relatórios com número real de agendamentos",
      icon: <Mail />,
    },
    {
      name: "Pesquisa de satisfação automática",
      icon: <Mail />,
    },
    {
      name: "Suporte humano sempre que precisar",
      icon: <Mail />,
    },
  ],
  pro: [
    {
      name: "Agendamentos automáticos 24h por dia",
      icon: <Mail />,
    },
    {
      name: "Leads qualificados direto no WhatsApp",
      icon: <Mail />,
    },
    {
      name: "Aumento de até 50% nos agendamentos",
      icon: <Mail />,
    },
    {
      name: "Lembretes que reduzem faltas",
      icon: <Mail />,
    },
    {
      name: "Follow-ups automatizados que reativam conversas",
      icon: <Mail />,
    },
    {
      name: "IA integrada com sua equipe",
      icon: <Mail />,
    },
    {
      name: "Relatórios com número real de agendamentos",
      icon: <Mail />,
    },
    {
      name: "Pesquisa de satisfação automática",
      icon: <Mail />,
    },
    {
      name: "Suporte humano sempre que precisar",
      icon: <Mail />,
    },
  ],
}

export function PlanSelector({ title }: { title?: string }) {
  const { currentSubscription, plans, actions } = useSubscription()
  const { isLoading, selectPlan } = useBillingStore()
  const usePagarme = useTypedFeatureFlag("use_pagarme_gateway", false)

  useEffect(() => {
    if (!currentSubscription && !plans) {
      actions.fetchBillingInfo()
    }
  }, [actions, currentSubscription, plans])

  const handleSelectPlan = (plan: Plan) => {
    selectPlan(plan)
  }

  // const filteredPlans = plans?.filter(plan => plan.interval === billingInterval) || []

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="relative rounded-lg border p-6 transition-all hover:shadow-md">
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h4 className="text-xl font-semibold">Plano Mensal</h4>
              <div className="mt-2 flex items-baseline">
                <span className="text-3xl font-bold">R$ 197,00</span>
                <span className="text-muted-foreground ml-1">/mês</span>
              </div>
            </div>
          </div>

          <ul className="mb-6 space-y-3">
            {FEATURES_BY_PLAN_TYPE.basic.map(feature => (
              <li
                key={feature.name}
                className={`flex items-start ${feature.disabled ? "opacity-50" : ""}`}
              >
                <CheckCircle
                  className={`mt-0.5 mr-2 h-5 w-5 ${feature.disabled ? "text-gray-400" : "text-green-500"}`}
                />
                <span className={`text-sm ${feature.bold ? "font-bold" : ""}`}>{feature.name}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={() => {
              if (usePagarme) {
                const monthlyPlan = plans?.find(p => p.interval === "monthly")
                if (monthlyPlan) handleSelectPlan(monthlyPlan)
              } else {
                window.open("https://cutt.ly/ctqdAN65", "_blank")
              }
            }}
            disabled={isLoading}
            variant={"default"}
            className={`w-full`}
            size="lg"
          >
            {isLoading ? "Processando..." : "Experimentar Plano"}
          </Button>
        </div>
        <div className="border-primary/30 bg-primary/5 relative rounded-lg border p-6 transition-all hover:shadow-md">
          <Badge className="bg-primary absolute -top-2 -right-2 px-3 py-1 font-medium text-white">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Popular
          </Badge>

          <div className="mb-5 flex items-start justify-between">
            <div>
              <h4 className="text-xl font-semibold">Plano Anual</h4>
              <div className="mt-2 flex items-baseline">
                <span className="text-muted-foreground ml-1">12x de &nbsp;</span>
                <span className="text-3xl font-bold">R$ 157,60</span>
                <span className="text-muted-foreground ml-1">/ano</span>
              </div>
            </div>
          </div>

          <ul className="mb-6 space-y-3">
            {FEATURES_BY_PLAN_TYPE.pro.map(feature => (
              <li
                key={feature.name}
                className={`flex items-start ${feature.disabled ? "opacity-50" : ""}`}
              >
                <CheckCircle
                  className={`mt-0.5 mr-2 h-5 w-5 ${feature.disabled ? "text-gray-400" : "text-green-500"}`}
                />
                <span className={`text-sm ${feature.bold ? "font-bold" : ""}`}>{feature.name}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={() => {
              if (usePagarme) {
                const yearlyPlan = plans?.find(p => p.interval === "yearly")
                if (yearlyPlan) handleSelectPlan(yearlyPlan)
              } else {
                window.open("https://cutt.ly/BtqdStgk", "_blank")
              }
            }}
            disabled={isLoading}
            variant={"default"}
            className={`w-full`}
            size="lg"
          >
            {isLoading ? "Processando..." : "Experimentar Plano"}
          </Button>
        </div>
      </div>
      {/* <div className={title ? `mb-4 flex items-center justify-between` : ""}>
        {title && <h3 className="text-lg font-medium">{title}</h3>}
        <div className="mb-6 flex justify-center">
          <Tabs
            defaultValue="monthly"
            value={billingInterval}
            onValueChange={value => setBillingInterval(value as SubscriptionInterval)}
            className="w-fit"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="monthly" className="px-8">
                Mensal
              </TabsTrigger>
              <TabsTrigger value="yearly" className="px-8">
                Anual <span className="ml-1 text-xs font-normal text-green-600">(25% off)</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div> */}

      {/* <div className="grid gap-6 md:grid-cols-2">
        {filteredPlans.map((plan: Plan) => {
          const isPro = plan.type === "pro"
          const isCurrent =
            plan.id === currentSubscription?.planId &&
            !["canceled", "inactive"].includes(currentSubscription?.status)
          const features = FEATURES_BY_PLAN_TYPE[plan.type]

          return (
            <div
              key={plan.id}
              className={`relative rounded-lg border p-6 transition-all hover:shadow-md ${
                isPro ? "border-primary/30 bg-primary/5" : "hover:border-primary/50"
              }`}
            >
              {isPro && (
                <Badge className="bg-primary absolute -top-2 -right-2 px-3 py-1 font-medium text-white">
                  <Sparkles className="mr-1 h-3.5 w-3.5" /> Popular
                </Badge>
              )}

              <div className="mb-5 flex items-start justify-between">
                <div>
                  <h4 className="text-xl font-semibold">{plan.name}</h4>
                  <div className="mt-2 flex items-baseline">
                    <span className="text-3xl font-bold">{plan.formattedPrice}</span>
                    <span className="text-muted-foreground ml-1">
                      /{billingInterval === "yearly" ? "ano" : "mês"}
                    </span>
                  </div>
                </div>
              </div>

              <ul className="mb-6 space-y-3">
                {features.map(feature => (
                  <li
                    key={feature.name}
                    className={`flex items-start ${feature.disabled ? "opacity-50" : ""}`}
                  >
                    <CheckCircle
                      className={`mt-0.5 mr-2 h-5 w-5 ${feature.disabled ? "text-gray-400" : isPro ? "text-primary" : "text-green-500"}`}
                    />
                    <span className={`text-sm ${feature.bold ? "font-bold" : ""}`}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => selectPlan(plan)}
                disabled={isLoading || isCurrent}
                variant={isCurrent ? "secondary" : "default"}
                className={`w-full`}
                size="lg"
              >
                {isLoading ? "Processando..." : isCurrent ? "Plano atual" : "Selecionar"}
              </Button>
            </div>
          )
        })}
      </div> */}
    </div>
  )
}
